import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Flex, Icon, Image, Link, Text, Tooltip, chakra, useBreakpointValue } from '@chakra-ui/react';
import {
  FaArrowDown,
  FaArrowUp,
  FaCheckCircle,
  FaCopy,
  FaEdit,
  FaExternalLinkAlt,
  FaEye,
  FaStopCircle,
  FaTimesCircle,
  FaTrashAlt,
} from 'react-icons/fa';
import { FaClockRotateLeft } from 'react-icons/fa6';
import { MissingImageIcon } from '@netiva/classifieds-ui';

import { RouterLink } from '@/components/global';
import { AdMessagesButton } from '../../AdMessagesButton';
import { PlatformIcon } from '@/components/ui';
import { useCurrencyFormatter, useDate, useMatomoTagManager } from '@/hooks';
import { routes } from '@/lib/routes';
import { CommonStepKeys } from '@/pages/Ad/constants';
import { ListActionButton } from './ListActionButton';
import type { AdListItemActionFunc, AdListItem as AdListItemType } from '../../../types';
import { ListStatusBadge } from './ListStatusBadge';
import { Tag } from './Tag';
import { useAdListSearchParams } from '@/pages/MyAds/hooks';

export type AdListItemProps = {
  item: AdListItemType;
  onActionExecuted: AdListItemActionFunc;
  showPlatform?: boolean;
  tabStatus?: string;
  hasMemberContract?: boolean;
};

const Wrapper = chakra(Box, {
  baseStyle: {
    position: 'relative',
    borderWidth: '1px',
    borderColor: 'neutral.100',
    borderRadius: 'default',
    flexDirection: { base: 'column', md: 'row' },
    boxShadow: 'sm',
    backgroundColor: 'white',
  },
});

const MainContainer = chakra(Flex, {
  baseStyle: {
    position: 'relative',
    gap: 2,
    flexDirection: { base: 'column', md: 'row' },
  },
});

const ImageContainer = chakra(Box, {
  baseStyle: {
    p: 2,
    position: 'relative',
    flex: { base: '0 0 100px', md: '0 0 200px' },
    height: { base: 'auto', md: '200px' },
  },
});

const StyledImage = chakra(Image, {
  baseStyle: {
    width: 'full',
    height: 'full',
    objectFit: 'cover',
    objectPosition: '0 0',
    // inset a small amount to hide the border for colored print ads
    objectViewBox: 'inset(2px)',
    borderRadius: 'default',
  },
});

const DetailsContainer = chakra(Flex, {
  baseStyle: {
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: 1,
    p: 4,
  },
});

const InfoContainer = chakra(Flex, {
  baseStyle: {
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: 1,
    p: 4,
    alignItems: { base: 'flex-start', md: 'flex-end' },
  },
});

const ActionsContainer = chakra(Flex, {
  baseStyle: {
    borderTopWidth: '1px',
    borderColor: 'neutral.100',
    p: 2,
    flex: '0 0 auto',
    flexDirection: { base: 'column', md: 'row' },
    alignItems: 'center',
  },
});

const SecondaryActions = chakra(Flex, {
  baseStyle: {
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});

const PrimaryAction = chakra(Flex, {
  baseStyle: {
    p: { base: 2, md: 0 },
    ml: { base: 0, md: 'auto' },
  },
});

export const AdListItem: FC<AdListItemProps> = ({
  item,
  onActionExecuted,
  showPlatform,
  tabStatus,
  hasMemberContract,
}) => {
  const { t } = useTranslation();
  const [searchParams] = useAdListSearchParams();

  const { formatRange } = useDate();
  const { format: formatCurrency } = useCurrencyFormatter(item.price.currency);

  const isNarrowLayout = useBreakpointValue({ base: true, lg: false });

  const { buildMatomoTrackingAttributes } = useMatomoTagManager();

  const publicationDates = useMemo(
    () => formatRange(item.publicationStart, item.publicationEnd),
    [formatRange, item.publicationEnd, item.publicationStart]
  );

  const price = useMemo(() => {
    if (item.price.onRequest) {
      return t('price.onRequest');
    }
    if (!item.price.amount) {
      return undefined;
    }
    if (item.price.vatClause) {
      return `${formatCurrency(item.price.amount)} ${item.price.vatClause}`;
    }
    return formatCurrency(item.price.amount);
  }, [formatCurrency, item, t]);

  const handleEditClick = (e: React.MouseEvent) => {
    if (item.allowEdit) {
      return;
    }

    e.preventDefault();
    onActionExecuted(item, 'showUneditableModal');
  };

  const editTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Edit_Listing',
    trackingName: `Edit_Listing_${tabStatus}`,
  });

  const deleteTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Delete_Listing',
    trackingName: `Delete_Listing_${tabStatus}`,
  });

  const activateTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Activate_Listing',
  });

  const deactivateTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Deactivate_Listing',
  });

  const duplicateTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Duplicate_Listing',
    trackingName: `Duplicate_Listing_${tabStatus}`,
  });

  const deactivateTopListingTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Dectivate_TopListing',
  });

  const detailsTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Open_Detail',
    trackingName: 'Open_Detail_MyListing',
  });

  return (
    <Wrapper>
      <MainContainer>
        <ImageContainer>
          <RouterLink to={item.allowEdit ? routes.ad(item.id, CommonStepKeys.edit) : '#'} onClick={handleEditClick}>
            {item.imageUrl ? (
              <StyledImage src={item.imageUrl} alt={item.title} />
            ) : (
              <Flex height="full" flexShrink={0} marginBottom={2} justify="center" align="center">
                <Icon as={MissingImageIcon} boxSize={16} />
              </Flex>
            )}
          </RouterLink>
        </ImageContainer>
        <DetailsContainer>
          <Flex align="center" gap={2}>
            {showPlatform && <PlatformIcon platformKey={item.platform || undefined} boxSize="16px" />}
            <Text fontSize="larger" fontWeight="bold">
              <RouterLink
                to={item.allowEdit ? routes.ad(item.id, CommonStepKeys.edit) : '#'}
                textDecoration="none"
                onClick={handleEditClick}
              >
                {item.title}
              </RouterLink>
            </Text>
          </Flex>
          {item.publicUrl && (
            <Link href={item.publicUrl} marginY={2} target="_blank" {...detailsTrackingProps}>
              <Icon as={FaExternalLinkAlt} marginRight={2} />
              {t('myads.publicDetail')}
            </Link>
          )}
          {item.category && <Text>{item.category}</Text>}
          {item.description && <Text py={1}>{item.description}</Text>}
          <Box mt="auto">
            {item.address && <Text>{item.address}</Text>}
            <Flex gap={4} align="center">
              {publicationDates && <Text>{publicationDates}</Text>}
              <Text py={1} fontWeight="bold">
                {price}
              </Text>
            </Flex>
          </Box>
        </DetailsContainer>
        <InfoContainer>
          <Flex align="flex-end" gap={2}>
            <ListStatusBadge item={item} />
            {item.isTopListed && <Tag backgroundColor="orange" label={t('myads.status.topListing')} />}
          </Flex>
          <Text fontSize="sm">{t('myads.dataObjectId', { id: item.id })}</Text>
          <Tooltip
            label={t('myads.views', { count: item.viewCount })}
            hasArrow
            placement="top"
            isDisabled={!isNarrowLayout}
          >
            <Flex align="center" gap={1} mb={2}>
              <Icon as={FaEye} boxSize="5" color="neutral.300" />
              <Text fontSize="sm" mt="auto">
                {isNarrowLayout ? item.viewCount : t('myads.views', { count: item.viewCount })}
              </Text>
            </Flex>
          </Tooltip>
        </InfoContainer>
      </MainContainer>
      <ActionsContainer>
        <SecondaryActions>
          {item.allowExtending && (
            <>
              <ListActionButton
                iconType={FaClockRotateLeft}
                label={t('myads.actions.extendDuration')}
                onClick={() => onActionExecuted(item, 'extendDuration')}
                variant="outline"
              />
              {!isNarrowLayout && <Divider h="6" mx="2" orientation="vertical" />}
            </>
          )}
          {item.allowEdit && (
            <>
              <ListActionButton
                iconType={FaEdit}
                label={t('myads.actions.edit')}
                onClick={() => onActionExecuted(item, 'edit')}
                {...editTrackingProps}
              />
              {!isNarrowLayout && <Divider h="6" mx="2" orientation="vertical" />}
            </>
          )}
          {item.allowDelete && (
            <>
              <ListActionButton
                iconType={FaTrashAlt}
                label={t('myads.actions.delete')}
                onClick={() => onActionExecuted(item, 'delete')}
                {...deleteTrackingProps}
              />
              {!isNarrowLayout && <Divider h="6" mx="2" orientation="vertical" />}
            </>
          )}
          {item.allowEnable && (
            <>
              <ListActionButton
                iconType={FaCheckCircle}
                label={t('myads.actions.activate')}
                onClick={() => onActionExecuted(item, 'enable')}
                {...activateTrackingProps}
              />
              {!isNarrowLayout && <Divider h="6" mx="2" orientation="vertical" />}
            </>
          )}
          {item.allowDisable && (
            <>
              <ListActionButton
                iconType={FaTimesCircle}
                label={t('myads.actions.deactivate')}
                onClick={() => onActionExecuted(item, 'disable')}
                {...deactivateTrackingProps}
              />
              {!isNarrowLayout && <Divider h="6" mx="2" orientation="vertical" />}
            </>
          )}
          {item.allowStop && (
            <>
              <ListActionButton
                iconType={FaStopCircle}
                label={t('myads.actions.stop')}
                onClick={() => onActionExecuted(item, 'stop')}
              />
              {!isNarrowLayout && <Divider h="6" mx="2" orientation="vertical" />}
            </>
          )}
          <ListActionButton
            iconType={FaCopy}
            label={t('myads.actions.duplicate')}
            onClick={() => onActionExecuted(item, 'duplicate')}
            {...duplicateTrackingProps}
          />
          {!isNarrowLayout && <Divider h="6" mx="2" orientation="vertical" />}
          <AdMessagesButton item={item} />
        </SecondaryActions>

        {hasMemberContract && item.allowTopListing && searchParams.status === 'active' && (
          <PrimaryAction>
            {item.isTopListed ? (
              <ListActionButton
                iconType={FaArrowDown}
                label={t('myads.actions.disableTopListing')}
                onClick={() => onActionExecuted(item, 'disableTopListing')}
                variant="solid"
                {...deactivateTopListingTrackingProps}
              />
            ) : (
              <ListActionButton
                iconType={FaArrowUp}
                label={t('myads.actions.enableTopListing')}
                onClick={() => onActionExecuted(item, 'enableTopListing')}
                variant="emphasis"
              />
            )}
          </PrimaryAction>
        )}
      </ActionsContainer>
    </Wrapper>
  );
};
